import axios from 'axios';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { t } from '../i18n';
import { trackEvent } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Calculate costs based on form data
 */
export const calculateCosts = async (formData, setCalculationResult, setCurrentStep, setLoading) => {
  try {
    setLoading(true);
    const response = await axios.post(`${API}/calculate`, {
      plz: formData.plz || null,
      location_name: formData.location_name || null,
      country: formData.country,
      project_type: formData.project_type,
      building_type: formData.building_type,
      area_m2: formData.area_m2 ? parseFloat(formData.area_m2) : null,
      persons: formData.persons ? parseInt(formData.persons) : null,
      heating_hot_water: formData.heating_hot_water,
      altitude_m: formData.altitude_m ? parseInt(formData.altitude_m) : null,
      prev_system: formData.prev_system || null,
      prev_consumption_kwh: formData.prev_consumption_kwh ? parseFloat(formData.prev_consumption_kwh) : null,
      energy_consumption_kwh: formData.energy_consumption_kwh ? parseFloat(formData.energy_consumption_kwh) : null,
      energy_includes_hot_water: formData.energy_includes_hot_water,
      free_cooling: formData.free_cooling,
      has_subsidy: formData.has_subsidy,
      subsidy_amount_erdwaerme: formData.subsidy_amount_erdwaerme ? parseFloat(formData.subsidy_amount_erdwaerme) : null,
      subsidy_amount_luftwaerme: formData.subsidy_amount_luftwaerme ? parseFloat(formData.subsidy_amount_luftwaerme) : null
    });
    setCalculationResult(response.data);
    setCurrentStep(5);
  } catch (error) {
    toast.error(t('toastCalcError'));
  } finally {
    setLoading(false);
  }
};

/**
 * Submit lead with form data and files
 */
export const submitLead = async (formData, calculationResult, files, navigate, setLoading) => {
  if (!formData.consent) {
    toast.error(t('toastConsentRequired'));
    return;
  }

  try {
    setLoading(true);
    const formDataToSend = new FormData();
    
    const leadData = {
      plz: formData.plz,
      country: formData.country,
      project_type: formData.project_type,
      building_type: formData.building_type,
      building_type_other: formData.building_type_other || null,
      area_m2: formData.area_m2 ? parseFloat(formData.area_m2) : null,
      persons: formData.persons ? parseInt(formData.persons) : null,
      heating_hot_water: formData.heating_hot_water,
      altitude_m: formData.altitude_m ? parseInt(formData.altitude_m) : null,
      prev_system: formData.prev_system || null,
      prev_consumption_kwh: formData.prev_consumption_kwh ? parseFloat(formData.prev_consumption_kwh) : null,
      energy_consumption_kwh: formData.energy_consumption_kwh ? parseFloat(formData.energy_consumption_kwh) : null,
      energy_includes_hot_water: formData.energy_includes_hot_water,
      free_cooling: formData.free_cooling,
      has_subsidy: formData.has_subsidy,
      subsidy_amount_erdwaerme: formData.subsidy_amount_erdwaerme ? parseFloat(formData.subsidy_amount_erdwaerme) : null,
      subsidy_amount_luftwaerme: formData.subsidy_amount_luftwaerme ? parseFloat(formData.subsidy_amount_luftwaerme) : null,
      has_installer: formData.has_installer,
      installer_info: formData.installer_info || null,
      timeframe: formData.timeframe,
      additional_info: formData.additional_info || null,
      demand_kwh: calculationResult?.demand_kwh || null,
      invest_lwp_eur: calculationResult?.invest_lwp_eur || null,
      invest_geo_std_eur: calculationResult?.invest_geo_std_eur || null,
      invest_separatus_eur: calculationResult?.invest_separatus_eur || null,
      annual_cost_lwp_eur: calculationResult?.annual_cost_lwp_eur || null,
      annual_cost_separatus_eur: calculationResult?.annual_cost_separatus_eur || null,
      annual_savings_eur: calculationResult?.annual_savings_eur || null,
      amortization_years: calculationResult?.amortization_years || null,
      currency: calculationResult?.currency || 'EUR',
      display_locale: calculationResult?.display_locale || 'de-DE',
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      address: formData.address || null,
      consent_version: '1.0',
      lang: localStorage.getItem('separatus_lang') || (navigator.language?.startsWith('de') ? 'de' : 'en'),
      calc_json: calculationResult ? JSON.stringify(calculationResult) : null
    };

    formDataToSend.append('data', JSON.stringify(leadData));
    files.forEach(file => {
      formDataToSend.append('files', file);
    });

    await axios.post(`${API}/leads`, formDataToSend, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    trackEvent('lead_submit');
    toast.success(t('toastSubmitSuccess'));
    setTimeout(() => navigate('/'), 2000);
  } catch (error) {
    console.error('Submit error:', error);
    console.error('Error response:', error.response?.data);
    const errorMsg = error.response?.data?.detail || t('toastSubmitError');
    toast.error(errorMsg);
  } finally {
    setLoading(false);
  }
};

/**
 * Generate PDF from results
 */
export const generatePDF = async (resultsRef, formData, setGeneratingPDF) => {
  if (!resultsRef.current) return;
  
  try {
    setGeneratingPDF(true);
    toast.info(t('toastPdfCreating'));
    
    // Lade den Informationstext
    let infoTextHTML = '';
    try {
      const infoResponse = await axios.get(`${API}/calculation-info`);
      infoTextHTML = infoResponse.data.content || '';
    } catch (error) {
      console.warn('Could not load info text for PDF:', error);
    }
    
    const element = resultsRef.current;
    
    // Clone element um Original nicht zu verändern
    const clonedElement = element.cloneNode(true);
    
    // PDF-optimierte Breite - größer als vorher
    clonedElement.style.width = '1200px';
    clonedElement.style.padding = '1rem';
    clonedElement.style.boxSizing = 'border-box';
    
    // Passe alle Cards an mit zuverlässigem Selector
    const systemCards = clonedElement.querySelectorAll('[style*="background: #E8F5E9"], [style*="background: #FFF3E0"], [style*="background: #E3F2FD"]');
    systemCards.forEach(card => {
      card.style.maxWidth = '100%';
      card.style.overflow = 'hidden';
    });
    
    // Entferne den "Weitere Informationen" Link
    const infoLink = clonedElement.querySelector('[data-testid="info-link"]');
    if (infoLink) {
      const infoLinkContainer = infoLink.closest('div[style*="textAlign: center"]');
      if (infoLinkContainer) {
        infoLinkContainer.remove();
      } else {
        infoLink.remove();
      }
    }
    
    // Ersetze alle "i" Icons mit "(Siehe Anhang)"
    const allElements = clonedElement.querySelectorAll('*');
    allElements.forEach(el => {
      const testId = el.getAttribute('data-testid');
      if (testId && testId.includes('info-icon')) {
        const replacementText = document.createElement('span');
        replacementText.textContent = ' ' + t('pdfSeeAppendix');
        replacementText.style.fontSize = '0.75rem';
        replacementText.style.color = '#666';
        replacementText.style.fontStyle = 'italic';
        replacementText.style.fontWeight = 'normal';
        el.replaceWith(replacementText);
      }
      
      // Entferne Tooltips
      const style = el.getAttribute('style') || '';
      if (style.includes('position: absolute') || style.includes('position:absolute')) {
        const text = el.textContent || '';
        if (text.includes('Vollkosten beinhalten') || 
            text.includes('anteiligen Investitionskosten') || 
            text.includes('50 %') ||
            text.includes('Erneuerungskosten fallen') ||
            text.length > 100) {
          el.remove();
        }
      }
    });
    
    // Temporär zum DOM hinzufügen
    clonedElement.style.position = 'absolute';
    clonedElement.style.left = '-9999px';
    clonedElement.style.top = '0';
    document.body.appendChild(clonedElement);
    
    // Warte auf Layout + Bilder
    await new Promise(resolve => setTimeout(resolve, 800));
    
    const canvas = await html2canvas(clonedElement, {
      scale: 1.5,
      useCORS: true,
      logging: false,
      backgroundColor: '#f5f5f5',
      windowWidth: 1200,
      scrollX: 0,
      scrollY: 0
    });
    
    document.body.removeChild(clonedElement);
    
    const imgData = canvas.toDataURL('image/jpeg', 0.82);
    const pdf = new jsPDF('p', 'mm', 'a4');
    
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    
    // Minimale Margins für maximalen Platz
    const margin = 4;
    const availableWidth = pdfWidth - (margin * 2);
    const availableHeight = pdfHeight - (margin * 2);
    const ratio = availableWidth / imgWidth;
    const scaledHeight = imgHeight * ratio;
    
    if (scaledHeight <= availableHeight) {
      pdf.addImage(imgData, 'JPEG', margin, margin, imgWidth * ratio, scaledHeight);
    } else {
      let remainingHeight = imgHeight;
      let sourceY = 0;
      const pageContentHeight = availableHeight / ratio;
      
      while (remainingHeight > 0) {
        const sliceHeight = Math.min(pageContentHeight, remainingHeight);
        
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = imgWidth;
        pageCanvas.height = sliceHeight;
        const ctx = pageCanvas.getContext('2d');
        ctx.drawImage(canvas, 0, sourceY, imgWidth, sliceHeight, 0, 0, imgWidth, sliceHeight);
        
        const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.82);
        pdf.addImage(pageImgData, 'JPEG', margin, margin, imgWidth * ratio, sliceHeight * ratio);
        
        remainingHeight -= sliceHeight;
        sourceY += sliceHeight;
        
        if (remainingHeight > 0) {
          pdf.addPage();
        }
      }
    }
    
    // Füge Informationstext als Anhang hinzu
    if (infoTextHTML && infoTextHTML.trim() !== '' && infoTextHTML !== '<p></p>') {
      pdf.addPage();
      
      const textMargin = 15;
      pdf.setFontSize(16);
      pdf.setFont(undefined, 'bold');
      pdf.text(t('pdfAppendixTitle'), textMargin, 20);
      
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = infoTextHTML;
      
      let y = 30;
      const lineHeight = 6;
      const maxWidth = pdfWidth - (textMargin * 2);
      
      const processNode = (node, isBold = false, isItalic = false, xOffset = 0) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent;
          if (text && text.trim()) {
            pdf.setFontSize(10);
            pdf.setFont(undefined, isBold ? 'bold' : (isItalic ? 'italic' : 'normal'));
            const lines = pdf.splitTextToSize(text.trim(), maxWidth - xOffset);
            lines.forEach(line => {
              if (y > pdfHeight - textMargin) {
                pdf.addPage();
                y = 20;
              }
              pdf.text(line, textMargin + xOffset, y);
              y += lineHeight;
            });
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const tagName = node.tagName.toLowerCase();
          
          if (tagName.match(/^h[1-6]$/)) {
            y += 4;
            const level = parseInt(tagName[1]);
            pdf.setFontSize(14 - level);
            pdf.setFont(undefined, 'bold');
            const text = node.textContent.trim();
            if (text) {
              if (y > pdfHeight - textMargin) {
                pdf.addPage();
                y = 20;
              }
              pdf.text(text, textMargin, y);
              y += lineHeight + 2;
            }
            return;
          }
          
          if (tagName === 'li') {
            if (y > pdfHeight - textMargin) { pdf.addPage(); y = 20; }
            pdf.setFontSize(10);
            pdf.setFont(undefined, 'normal');
            // Gesamten Text des li-Elements als Block rendern
            const fullText = node.textContent.trim();
            if (fullText) {
              const lines = pdf.splitTextToSize('• ' + fullText, maxWidth);
              lines.forEach((line, i) => {
                if (y > pdfHeight - textMargin) { pdf.addPage(); y = 20; }
                pdf.text(line, textMargin, y);
                y += lineHeight;
              });
            }
            y += 1;
            return;
          }

          // Für p, div etc.: Gesamten Textinhalt sammeln und als Block rendern
          // statt jeden Child-Node einzeln (verhindert Zeilenumbrüche bei [], () etc.)
          if (tagName === 'p') {
            if (y > 30) y += 3;
            const fullText = node.textContent.trim();
            if (fullText) {
              const hasBold = node.querySelector('strong, b');
              pdf.setFontSize(10);
              pdf.setFont(undefined, hasBold ? 'bold' : (isBold ? 'bold' : (isItalic ? 'italic' : 'normal')));
              const lines = pdf.splitTextToSize(fullText, maxWidth - xOffset);
              lines.forEach(line => {
                if (y > pdfHeight - textMargin) { pdf.addPage(); y = 20; }
                pdf.text(line, textMargin + xOffset, y);
                y += lineHeight;
              });
            }
            y += 2;
            return;
          }
          
          const childIsBold = isBold || tagName === 'strong' || tagName === 'b';
          const childIsItalic = isItalic || tagName === 'em' || tagName === 'i';
          
          node.childNodes.forEach(child => {
            processNode(child, childIsBold, childIsItalic, xOffset);
          });
          
          if (tagName === 'ul' || tagName === 'ol') {
            y += 2;
          }
        }
      };
      
      tempDiv.childNodes.forEach(node => processNode(node));
    }
    
    pdf.save(`${t('pdfFileName')}_${formData.plz}_${new Date().toISOString().split('T')[0]}.pdf`);
    
    trackEvent('pdf_generate');
    toast.success(t('toastPdfSuccess'));
  } catch (error) {
    console.error('PDF generation error:', error);
    toast.error(t('toastPdfError'));
  } finally {
    setGeneratingPDF(false);
  }
};

/**
 * Load calculation info for popup
 */
export const loadCalculationInfo = async (setInfoContent, setShowInfoPopup) => {
  try {
    const response = await axios.get(`${API}/calculation-info`);
    setInfoContent(response.data.content || '');
    setShowInfoPopup(true);
  } catch (error) {
    console.error('Error loading calculation info:', error);
    toast.error(t('toastInfoLoadError'));
  }
};
